// `search.page.tpl.html:595` — three columns of checkboxes: degree, field of study, and
// institute badges.
//
// ⚠️ THE FIRST CHECKBOX OF THE FIRST TWO COLUMNS IS AN "ANY" THAT DISABLES THE REST.
// `onAnyEducationSectionChecked` ticks the group's "Any" and every other box in that column
// goes `ng-disabled` — they keep whatever value they had, they simply stop responding. So the
// state is not "Any means all selected"; it is "Any overrides, and what is underneath is
// preserved for when it is unticked again". Clearing the others on tick — which is the
// intuitive read — loses a user's selection the moment they try "Any" and change their mind.
//
// The "Any" option is a group like the others (`anyDegreeGroup`, `anyFieldGroup`) and is keyed
// by the same `key`, so the criteria object holds it alongside the real degrees rather than in
// a separate flag.
//
// ⚠️ AND THE THIRD COLUMN IS NOT LIKE THE OTHER TWO. Institute badges have no "Any", their
// labels come from `text` rather than `key`, and the column ends with a checkbox bound to a
// different part of the criteria entirely (`degreeOrIDF`, not `instituteBadge[…]`).
import type { ReactElement } from 'react'
import { WooCheckbox } from '../common/WooCheckbox.tsx'

export interface EducationGroup {
  key: string
  /**
   * `IEducationGroup.values` — the wire values this display group folds together.
   *
   * ⚠️ THE SECTION DOES NOT READ IT; THE LOADER DOES. A saved alert stores per-VALUE flags
   * ("BA", "BSC") and the form shows per-GROUP checkboxes ("Bachelor"), so `loadEducationSection`
   * needs the membership to fold one into the other. Optional because the section renders
   * correctly without it and the groups arrive from a bootstrap field that is not carried yet.
   */
  values?: string[]
}

export interface InstituteBadge {
  key: string
  text: string
}

export interface EducationCriteria {
  degreeType?: Record<string, boolean>
  fieldCategory?: Record<string, boolean>
  instituteBadge?: Record<string, boolean>
  /** Its own field, not a badge — see the note above. */
  degreeOrIDF?: boolean
}

export interface EducationSectionProps {
  criteria: EducationCriteria
  anyDegreeGroup: EducationGroup
  degreeGroups: readonly EducationGroup[]
  anyFieldGroup: EducationGroup
  fieldGroups: readonly EducationGroup[]
  instituteBadges: readonly InstituteBadge[]
  onChange?: (criteria: EducationCriteria) => void
}

export function EducationSection({
  criteria,
  anyDegreeGroup,
  degreeGroups,
  anyFieldGroup,
  fieldGroups,
  instituteBadges,
  onChange,
}: EducationSectionProps): ReactElement {
  const setIn = (field: 'degreeType' | 'fieldCategory' | 'instituteBadge', key: string, value: boolean): void => {
    onChange?.({ ...criteria, [field]: { ...criteria[field], [key]: value } })
  }

  /**
   * `onAnyEducationSectionChecked` (`search.page.component.ng1.ts:1643`) — ticking "Any" sets
   * EVERY sibling to the same value, and unticking it clears them all.
   *
   * ⚠️ THE SIBLINGS DO NOT KEEP THEIR OWN VALUES, which is what this port assumed: it
   * disabled them and left them alone, so "Any degree" showed one tick beside three empty
   * boxes where production shows four. The note that stood here said the value underneath
   * survives; upstream overwrites it in the same breath as the flag.
   */
  const setAny = (
    field: 'degreeType' | 'fieldCategory',
    anyKey: string,
    groups: readonly EducationGroup[],
    value: boolean,
  ): void => {
    const next: Record<string, boolean> = { ...criteria[field], [anyKey]: value }
    for (const group of groups) next[group.key] = value
    onChange?.({ ...criteria, [field]: next })
  }

  const anyDegree = criteria.degreeType?.[anyDegreeGroup.key] === true
  const anyField = criteria.fieldCategory?.[anyFieldGroup.key] === true

  return (
    <div className="education-criteria sub-section form-inline-top clearfix">
      <div className="form-group edu-section degree">
        <WooCheckbox
          checked={anyDegree}
          onChange={(checked) => setAny('degreeType', anyDegreeGroup.key, degreeGroups, checked)}
        >
          {anyDegreeGroup.key}
        </WooCheckbox>
        {degreeGroups.map((group) => (
          <WooCheckbox
            key={group.key}
            className="disabled-education-cb"
            checked={criteria.degreeType?.[group.key] === true}
            disabled={anyDegree}
            onChange={(checked) => setIn('degreeType', group.key, checked)}
          >
            {group.key}
          </WooCheckbox>
        ))}
      </div>

      <div className="form-group edu-section fos">
        <WooCheckbox
          checked={anyField}
          onChange={(checked) => setAny('fieldCategory', anyFieldGroup.key, fieldGroups, checked)}
        >
          {anyFieldGroup.key}
        </WooCheckbox>
        {fieldGroups.map((group) => (
          <WooCheckbox
            key={group.key}
            className="disabled-education-cb"
            checked={criteria.fieldCategory?.[group.key] === true}
            disabled={anyField}
            onChange={(checked) => setIn('fieldCategory', group.key, checked)}
          >
            {group.key}
          </WooCheckbox>
        ))}
      </div>

      <div className="form-group edu-section institute-badge">
        {instituteBadges.map((badge) => (
          <WooCheckbox
            key={badge.key}
            checked={criteria.instituteBadge?.[badge.key] === true}
            onChange={(checked) => setIn('instituteBadge', badge.key, checked)}
          >
            {/* `text`, not `key` — the only column whose label is a different field. */}
            {badge.text}
          </WooCheckbox>
        ))}
        <WooCheckbox
          checked={criteria.degreeOrIDF === true}
          onChange={(degreeOrIDF) => onChange?.({ ...criteria, degreeOrIDF })}
        >
          Allow special tech units instead of a degree
        </WooCheckbox>
      </div>
    </div>
  )
}
